// The page's entry point (`index.html`): fill the project-payload cache, THEN load the app.
//
// Project payloads live in IndexedDB since 08.10 (`storage/payloadStore.ts`), which only answers
// asynchronously, while the app reads them synchronously from dozens of places — some during
// render, some at module load (`main.tsx` alone reads the timeline for its diagnostics and renames
// scenarios before the first paint). So nothing of the app is imported until the cache is full:
// `main.tsx` is loaded with a dynamic `import()` only after `hydratePayloadStore()` resolves, and no
// screen can ever render an empty project because hydration had not finished.
//
// This file imports only the store, which imports nothing from the app.

import { hydratePayloadStore } from './storage/payloads'

function say(text: string): void {
  const root = document.getElementById('root')
  if (!root) return
  root.textContent = ''
  const p = document.createElement('p')
  p.style.cssText = 'font: 15px/1.5 system-ui, sans-serif; max-width: 560px; margin: 64px auto; padding: 0 16px;'
  p.textContent = text
  root.appendChild(p)
}

// Hydration is a few hundred milliseconds; the first load after this change also moves every
// project out of localStorage. Say something if it takes long enough to notice.
const slow = window.setTimeout(() => say('Opening projects…'), 1500)

void hydratePayloadStore()
  .then((report) => {
    window.clearTimeout(slow)
    if (report.unavailable) {
      // Projects were moved into IndexedDB on an earlier load, and now it will not open. Rendering
      // the app would show every project empty — and an editor that autosaves an empty project is
      // how a bad moment becomes a lost one. So the app is not loaded at all.
      say(
        'The browser could not open the project storage (IndexedDB), so the app is not starting: ' +
          'otherwise the projects would look empty. No data has been deleted. ' +
          'Reload the page; if that does not help, close every StoryDress tab, restart the browser and open the app again.',
      )
      return
    }
    return import('./main.tsx')
  })
  .catch((err: unknown) => {
    window.clearTimeout(slow)
    console.error('[bootstrap] the app failed to start', err)
    say(`The app failed to start: ${err instanceof Error ? err.message : String(err)}. Reload the page.`)
  })
